import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import en from '../lib/data/en.json';
import pt from '../lib/data/pt.json';

const LanguageContext = createContext();

const translations = {
  en: {
    ...en,
    pricing: {
      ...(en.pricing || {}),
      noFees: en?.pricing?.noFees || 'No setup fees. Cancel anytime.',
    },
  },
  pt: {
    ...pt,
    pricing: {
      ...(pt.pricing || {}),
      noFees: pt?.pricing?.noFees || 'Sem taxa de configuração. Cancele quando quiser.',
    },
  },
};

export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English', short: 'EN', flag: '🇺🇸' },
  { code: 'pt', label: 'Português', short: 'PT', flag: '🇧🇷' },
];

const lookup = (bundle, keys) => {
  let cursor = bundle;
  for (const k of keys) {
    if (cursor == null || typeof cursor !== 'object' || !(k in cursor)) return undefined;
    cursor = cursor[k];
  }
  return cursor;
};

const interpolate = (template, params) => {
  if (typeof template !== 'string' || !params) return template;
  return template.replace(/\{(\w+)\}/g, (m, name) =>
    params[name] !== undefined ? String(params[name]) : m
  );
};

// The Brazil landing entry point (selit.ai/br) always opens in Portuguese,
// whatever the visitor last chose. Resolved from the URL before the first
// render, so the page never paints in English and then swaps.
export const BR_LANDING_PATH = /^\/br(\/|$)/i;

const isBrazilLandingUrl = () =>
  typeof window !== 'undefined' && BR_LANDING_PATH.test(window.location.pathname);

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    if (isBrazilLandingUrl()) return 'pt';
    const saved = localStorage.getItem('language');
    return saved && translations[saved] ? saved : 'en';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.setAttribute('lang', language === 'pt' ? 'pt-BR' : 'en');
  }, [language]);

  const setLanguage = useCallback((lang) => {
    if (translations[lang]) setLanguageState(lang);
  }, []);

  // t(key) returns the translation in the active language; if missing, falls
  // back to English; if still missing it uses a literal string default when the
  // caller passed one (t(key, 'Default')), otherwise returns the key itself so
  // the gap is visible in dev but a raw key path never reaches users.
  // t(key, { name: 'X' }) interpolates {name} placeholders.
  const t = useCallback(
    (key, params) => {
      if (!key) return '';
      const keys = String(key).split('.');
      const primary = lookup(translations[language], keys);
      if (primary !== undefined) return interpolate(primary, params);
      const fallback = lookup(translations.en, keys);
      if (fallback !== undefined) return interpolate(fallback, params);
      // Key missing in every bundle: prefer a caller-supplied string default
      // (t('some.key', 'Inventory')) over leaking the raw key path to the UI.
      if (typeof params === 'string') return params;
      return key;
    },
    [language]
  );

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, languages: SUPPORTED_LANGUAGES }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
